import React, { useState } from "react";
import { UseComboboxStateChange } from "downshift";

import { ComposableHeaderDropdown, IndexedObject, Search } from "@themuse/design-system";
import { fetchCompanySuggestions, CompanySuggestion } from "@util/api";
import { isBrowser } from "@util/isBrowser";
import { pushDataLayerEvent } from "@util/tracking";

import homepageStyles from "./HomepageSearchDropdown.module.scss";
import adviceHeaderStyles from "./AdviceHeaderSearchDropdown.module.scss";

export const formatCompanySuggestions = (companySuggestions: CompanySuggestion[]) =>
    companySuggestions.slice(0, 4).map((companyResult: CompanySuggestion) => {
        const { text, value } = companyResult;
        const { short_name } = value;
        return {
            label: `${text}`,
            value: `/profiles/${short_name}`
        };
});

export const updateInput = async (
    changes: UseComboboxStateChange<IndexedObject>,
    setItems: (items: IndexedObject[]) => void,
    setInputValue: (inputValue: string) => void,
    fetchData:(apiBase: string, inputValue: string) => Promise<CompanySuggestion[]>
) => {
    const { inputValue } = changes;
    const jobSearchItem = {
        label: `${inputValue}`,
        value: `/search/keyword/${inputValue}/`,
    };
    setInputValue(inputValue);
    if (inputValue) {
        setItems([jobSearchItem]);
        const companyResults = await fetchData("", inputValue);
        const companyList = companyResults?.length ? formatCompanySuggestions(companyResults) : [];
        setItems([jobSearchItem].concat(companyList));
    } else {
        setItems([]);
    }
};

interface SearchDropdownProps {
    pageLocation: string;
    showRebrand?: boolean
}

export const SearchDropdown = (props: SearchDropdownProps) => {
    const { pageLocation, showRebrand } = props;
    const [dropdownItems, setDropdownItems] = useState([] as IndexedObject[]);
    const [inputValue, setInputValue] = useState("");

    const searchQuerySubmittedEvent = (selectedItemText: string) => {
        const searchTerm = selectedItemText ? selectedItemText : inputValue;
        const eventProps = {
            search_term: searchTerm || ""
        };
        pushDataLayerEvent("searchQuerySubmitted", eventProps);
    };

    const onKeyboardSelectItem = (_: React.KeyboardEvent<HTMLInputElement>, selectedItem: IndexedObject) => {
        if (!isBrowser()) return "";

        const searchPath = inputValue
            ? `/search/keyword/${inputValue}`
            : "/search";
        const path = selectedItem?.value
            ? selectedItem?.value
            : searchPath;

        searchQuerySubmittedEvent(selectedItem?.label);

        return window.open(path, "_blank").focus();
    };

    const onMouseSelectItem = (_: React.MouseEvent<HTMLInputElement>, selectedItem: IndexedObject) => {
        searchQuerySubmittedEvent(selectedItem.label);
    };

    const adviceRebrand = showRebrand ? adviceHeaderStyles.rebrand : "";
    const homepageRebrand = showRebrand ? homepageStyles.rebrand : "";

    const homepageClasses = {
        container: `${homepageStyles.container} ${homepageRebrand}`,
        containerOpened: `${homepageStyles.containerOpened} ${homepageRebrand}`,
        inputContainer: homepageStyles.inputContainer,
        inputContainerOpened: homepageStyles.inputContainerOpened,
        input: homepageStyles.input,
        menuOpened: homepageStyles.menuOpened,
        menuList: homepageStyles.menuList,
        listItem: homepageStyles.listItem,
        listItemText: homepageStyles.listItemText,
        inputClearButton: homepageStyles.inputClearButton,
        postLabel: homepageStyles.postLabel,
    };

    const adviceClasses = {
        container: `${adviceHeaderStyles.container} ${adviceRebrand}`,
        containerOpened: `${adviceHeaderStyles.containerOpened} ${adviceRebrand}`,
        inputContainer: adviceHeaderStyles.inputContainer,
        inputContainerOpened: adviceHeaderStyles.inputContainerOpened,
        input: adviceHeaderStyles.input,
        menuOpened: adviceHeaderStyles.menuOpened,
        menuList: adviceHeaderStyles.menuList,
        listItem: adviceHeaderStyles.listItem,
        listItemText: adviceHeaderStyles.listItemText,
        closeButton: adviceHeaderStyles.closeButton,
        inputClearButton: adviceHeaderStyles.inputClearButton,
        clearButton: adviceHeaderStyles.clearButton,
        postLabel: adviceHeaderStyles.postLabel,
    };

    const dropdownProps = {
        classes: pageLocation === "homepage" ? homepageClasses : adviceClasses,
        showRebrand,
        dropdownProps: {
            IconComponent: <Search />,
            debounceWait: 10,
            dropdownList: dropdownItems,
            inputId: "search-input",
            onInputChange: (changes: UseComboboxStateChange<IndexedObject>) => updateInput(
                changes,
                setDropdownItems,
                setInputValue,
                fetchCompanySuggestions
            ),
            noResultsMessage: " ",
            onKeyboardSelectItem,
            onMouseSelectItem
        }
    };

    return <ComposableHeaderDropdown {...dropdownProps} />;
};
